import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Stack,
  Divider,
  Chip,
  Button
} from "@mui/material";
import { motion } from "framer-motion";
import {
  DirectionsCar,
  VerifiedUser,
  Restaurant,
  Hiking,
  Terrain,
  Star
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import TourCard from "../Components/Cards/TourCard";
import TopRatedLocations from "../Components/UI/TopRatedLocation";

/* =========================
   ALL TOUR PACKAGES DATA
========================= */
const tourPackages = [
  // Jammu & Kashmir
  {
    id: "jk1",
    title: "Kashmir Valley Classic Tour",
    places: "Srinagar • Gulmarg • Pahalgam • Sonmarg",
    price: "₹ 14,999/-",
    image: "https://www.flamingotravels.co.in/_next/image?url=https%3A%2F%2Fimgcdn.flamingotravels.co.in%2FImages%2FCity%2FDal-Lake.png&w=3840&q=90",
    rating: 4.8
  },
  {
    id: "jk2",
    title: "Kashmir Honeymoon Special",
    places: "Srinagar • Houseboat • Gulmarg",
    price: "₹ 19,999/-",
    image: "https://www.bharatbooking.com/admin/webroot/img/uploads/holiday-package/1701150890_728646-5n-6d-kashmir-luxury-honeymoon-package-image.webp",
    rating: 4.9
  },

  // Himachal Pradesh
  {
    id: "hp1",
    title: "Complete Himachal Tour",
    places: "Shimla • Manali • Kasol • Dharamshala",
    price: "₹ 22,999/-",
    image: "https://maashaktitours.com/wp-content/uploads/2019/06/mera-himachal.jpg",
    rating: 4.7
  },
  {
    id: "hp2",
    title: "Spiti Valley Adventure",
    places: "Kaza • Key • Kibber • Chandratal",
    price: "₹ 29,999/-",
    image: "https://nomadictrails.in/wp-content/uploads/2023/08/wanderon-spiti-1-1.webp",
    rating: 4.9
  },

  // Uttarakhand
  {
    id: "uk1",
    title: "Char Dham Yatra",
    places: "Yamunotri • Gangotri • Kedarnath • Badrinath",
    price: "₹ 26,999/-",
    image: "https://uttarakhandtourism.gov.in/assets/media/UTDB_media_1740661833CHAR-DHAM.jpg",
    rating: 4.9
  },
  {
    id: "uk2",
    title: "Rishikesh Adventure Tour",
    places: "Rishikesh • Shivpuri • Neelkanth",
    price: "₹ 10,499/-",
    image: "https://makemybharatyatra.com/uploads/packages/1727346890.jpg",
    rating: 4.7
  },

  // Leh Ladakh
  {
    id: "ll1",
    title: "Leh Ladakh Road Trip",
    places: "Leh • Nubra • Pangong • Khardung La",
    price: "₹ 32,999/-",
    image: "https://media.traveljunky.in/website-assets/sub-navigation/ladakh-tour-packages/68ba9eb95025c.webp",
    rating: 4.8
  },
  {
    id: "ll2",
    title: "Ladakh Monastery & Lakes Tour",
    places: "Hemis • Thiksey • Pangong • Tso Moriri",
    price: "₹ 28,499/-",
    image: "https://www.lehladakhindia.com/wp-content/uploads/2013/04/pangong-lake-ladakh-1.jpg",
    rating: 4.9
  }
];

/* =========================
   ENRICH TOUR
========================= */
const enrichTour = (tour, index) => {
  const durations = ["5D / 4N", "6D / 5N", "7D / 6N"];
  const tags = ["Bestseller", "Popular", "Limited Offer"];

  return {
    ...tour,
    duration: durations[index % durations.length],
    tag: tags[index % tags.length],
    oldPrice: parseInt(tour.price.replace(/[₹,/-]/g, "")) + 5000,
    discountEndsIn: "Limited Time Offer"
  };
};

const Tourpackages = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: "#f7fdf9", minHeight: "100vh", px: { xs: 2, md: 6 }, py: 6 }}>

      {/* ================= HERO ================= */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
        <Box
          sx={{
            mb: 8,
            textAlign: "center",
            py: 7,
            borderRadius: 4,
            color: "#fff",
            background:
              "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2000)",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <Chip
            icon={<Terrain />}
            label="ALL INDIA TOUR PACKAGES"
            sx={{ mb: 2, bgcolor: "#16a34a", color: "#fff", fontWeight: 700 }}
          />
          <Typography variant="h3" fontWeight={900}>
            Explore India With Confidence
          </Typography>
          <Typography maxWidth="800px" mx="auto" mt={2} lineHeight={1.8}>
            From the valleys of Kashmir to the deserts of Ladakh,  
            from Himachal’s mountains to Uttarakhand’s spirituality —  
            we design journeys that are safe, comfortable, and unforgettable.
          </Typography>
        </Box>
      </motion.div>

      {/* ================= TOP RATED ================= */}
      <TopRatedLocations />

      {/* ================= PACKAGES ================= */}
      <Typography variant="h4" fontWeight={800} mb={3}>
        Our Most Popular Tour Packages
      </Typography>

      <Grid container spacing={3} mb={10}>
        {tourPackages.map((tour, index) => (
          <Grid item xs={12} sm={6} md={3} key={tour.id}>
            <TourCard
              tour={tour}
              index={index}
              onViewDetails={() =>
                navigate("/tourdetails", {
                  state: enrichTour(tour, index)
                })
              }
              onEnquire={() => navigate("/booking")}
            />
          </Grid>
        ))}
      </Grid>

      {/* ================= WHY CHOOSE US ================= */}
      <Typography variant="h4" fontWeight={800} mb={4}>
        Why Book With Us
      </Typography>

      <Grid container spacing={4} mb={10}>
        {[
          {
            icon: <VerifiedUser />,
            title: "Trusted & Verified",
            text: "Licensed operators, verified hotels, and experienced local guides."
          },
          {
            icon: <Restaurant />,
            title: "Meals Included",
            text: "Hygienic meals with local flavors and vegetarian options."
          },
          {
            icon: <DirectionsCar />,
            title: "Comfortable Travel",
            text: "Private cabs with skilled hill drivers for your safety."
          },
          {
            icon: <Hiking />,
            title: "Curated Experiences",
            text: "Balanced itineraries — not rushed, not boring."
          }
        ].map((item, i) => (
          <Grid item xs={12} md={3} key={i}>
            <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center", height: "100%" }}>
              <Avatar sx={{ bgcolor: "#e8f5e9", mx: "auto", mb: 2 }}>
                {item.icon}
              </Avatar>
              <Typography fontWeight={700} mb={1}>
                {item.title}
              </Typography>
              <Typography color="text.secondary" lineHeight={1.7}>
                {item.text}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ================= CTA ================= */}
      <Paper
        sx={{
          p: 6,
          borderRadius: 4,
          background: "linear-gradient(135deg, #16a34a, #065f46)",
          color: "#fff"
        }}
      >
        <Stack spacing={3} alignItems="center">
          <Typography variant="h3" fontWeight={800}>
            Let’s Plan Your Perfect Trip
          </Typography>
          <Typography maxWidth="900px" textAlign="center" lineHeight={1.8}>
            Whether it’s a family vacation, honeymoon, spiritual journey,
            adventure trip, or a road expedition —
            our team is ready to craft it for you.
          </Typography>
          <Button
            size="large"
            sx={{
              bgcolor: "#FFD046",
              color: "#000",
              fontWeight: 700,
              px: 6,
              "&:hover": { bgcolor: "#FFC107" }
            }}
            onClick={() => navigate("/booking")}
          >
            Enquire Now
          </Button>
        </Stack>
      </Paper>

    </Box>
  );
};

export default Tourpackages;
